import React, { useEffect, useState } from 'react';
import { getInfoAlunoByCpf } from '../../Api';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFormularioStorage } from '../../storage';
import DocValido from '../../components/Carteiras/DocValido';
import './Certificado.css';
import LoadingUI from '../../components/LoadingUI';
import { notify } from '../../components/Notify';
import Certificado2 from '../../components/Carteiras/Certificado';
import FooterHome from '../Home2/components/FooterHome';
import { whatsappPhoneOpen } from '../../utils';
import { RECAPTCHA_SITE_KEY } from '../../config/recaptcha';

export default function Certificado() {
    const { cpf } = useParams();
    const location = useLocation();
    const { setUserData } = useFormularioStorage(state => state.dispatch);

    // Portão de captcha: só carrega/mostra os dados depois de confirmar que é
    // humano. Quem chega pela /validacao (state.validado) já passou por um
    // captcha verificado no servidor → entra direto. Sem site key (dev), libera.
    // Em localhost também libera direto: a site key do reCAPTCHA não autoriza
    // localhost como domínio, então o widget quebra no teste local.
    const ehLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const liberadoInicial = !RECAPTCHA_SITE_KEY || ehLocalhost || !!location.state?.validado;
    const [liberado, setLiberado] = useState(liberadoInicial);

    const [loading, setLoading] = useState(liberadoInicial);
    const [isValido, setIsValido] = useState(true); // Estado para controlar a validade
    const navigate = useNavigate();

    // Configure o número do WhatsApp aqui (apenas números, com DDD)
    const WHATSAPP_NUMBER = "31997333998";
    const WHATSAPP_MESSAGE = encodeURIComponent("Olá! Minha carteirinha venceu e gostaria de solicitar a renovação.");

    useEffect(() => {
        // Só busca os dados depois de liberar o captcha.
        if (!liberado) return;
        setLoading(true);
        const controller = new AbortController();

        function verificarValidade(anoCarteirinha) {
            const anoBase = Number(anoCarteirinha);
            if (!anoBase || Number.isNaN(anoBase)) return true;

            const dataAtual = new Date();
            dataAtual.setHours(0, 0, 0, 0);

            const dataLimite = new Date(anoBase + 1, 2, 31);
            return dataAtual <= dataLimite;
        }

        getInfoAlunoByCpf({ cpf, query: '', signal: controller.signal })
            .then(res => {
                setUserData(res.data);

                const valido = verificarValidade(res.data.ano);
                setIsValido(valido);

                if (!valido) {
                    notify('Esta carteirinha expirou!', 'warning');
                }
            })
            .catch(err => {
                if (controller.signal.aborted) return;
                console.error("Erro ao carregar dados do aluno:", err);
                notify('Erro ao carregar dados do aluno!', 'error');
                navigate('/');
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [cpf, navigate, setUserData, liberado]);

    // Portão: enquanto não liberar o captcha, não mostra (nem busca) nada.
    if (!liberado) {
        return <CaptchaGate onLiberar={() => setLiberado(true)} />;
    }

    if (loading) return <LoadingUI />;

    // Tela de Carteirinha Vencida
    if (!isValido) {
        return (
            <div style={{ 
                height: '100vh', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#fff',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{ fontSize: '60px', marginBottom: '20px' }}>⚠️</div>
                <h2 style={{ color: '#333', marginBottom: '10px' }}>Carteirinha Vencida</h2>
                <p style={{ color: '#666', maxWidth: '300px', marginBottom: '30px' }}>
                    O prazo de validade deste documento expirou em 31 de março. 
                    Para continuar utilizando os benefícios, realize a renovação.
                </p>
                
                <button 
                    onClick={whatsappPhoneOpen}
                    style={{
                        backgroundColor: '#25D366',
                        color: 'white',
                        padding: '15px 25px',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)'
                    }}
                >
                    Renovar via WhatsApp
                </button>
                
                <button 
                    onClick={() => navigate('/')}
                    style={{ marginTop: '20px', background: 'none', border: 'none', color: '#888', cursor: 'pointer' }}
                >
                    Voltar para o início
                </button>
            </div>
        );
    }

    // Tela de Carteirinha Válida
    return (
        <div style={{ backgroundColor: '#fafafa', minHeight: '100vh' }}>
            <Certificado2 />
            <FooterHome />
            {/* <DocValido /> */}
        </div>
    );
}

// Portão de verificação humana antes de exibir os dados da carteirinha.
// Aparece quando a página é aberta direto (QR-Code / URL) sem ter passado
// pela /validacao. Ao resolver o reCAPTCHA, libera o carregamento.
function CaptchaGate({ onLiberar }) {
    return (
        <div style={gate.page}>
            <div style={gate.card}>
                <div style={gate.brandLogo}>U</div>
                <h1 style={gate.title}>Validação de carteirinha</h1>
                <p style={gate.subtitle}>
                    Por segurança, confirme que você não é um robô para visualizar
                    os dados desta carteirinha de estudante da UEN.
                </p>
                <div style={gate.captchaWrap}>
                    <ReCAPTCHA
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={(token) => { if (token) onLiberar(); }}
                        hl="pt-BR"
                    />
                </div>
            </div>
        </div>
    );
}

const gate = {
    page: {
        minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '24px 16px',
        background: 'linear-gradient(135deg, #047857 0%, #0D9488 55%, #059669 100%)',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
    },
    card: {
        width: '100%', maxWidth: 420, background: '#FFFFFF', borderRadius: 16,
        boxShadow: '0 24px 48px -16px rgba(2,44,34,0.35), 0 0 0 1px rgba(15,23,42,0.04)',
        padding: '34px 30px', textAlign: 'center',
    },
    brandLogo: {
        width: 52, height: 52, borderRadius: 14, margin: '0 auto 18px',
        background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
        color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 22,
    },
    title: { fontSize: '1.4rem', fontWeight: 800, color: '#0F172A', margin: '0 0 8px' },
    subtitle: { color: '#64748B', margin: '0 0 22px', fontSize: '0.92rem', lineHeight: 1.55 },
    captchaWrap: { display: 'flex', justifyContent: 'center' },
};